import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { siteUrl, supabase } from '@/lib/supabase';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SiteHeader } from '@/components/SiteHeader';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * Due passi, e nessun link nell'email.
 *
 * Dal 02/09/2026 si entra **solo col codice monouso**. Il link non è sparito
 * per gusto: la posta di un Comune passa da filtri aziendali che pre-aprono i
 * link per ispezionarli, e un link di accesso pre-aperto è una credenziale
 * monouso spesa da una macchina — l'operatore trova «non più valido» su un link
 * validissimo. E un'email inoltrata a un collega, cosa che negli uffici succede,
 * inoltra l'accesso insieme al messaggio.
 *
 * `emailRedirectTo` resta, e non contraddice niente: nell'email non produce più
 * nessun bottone. Serve a scegliere il TESTO — i template su Supabase mostrano
 * il ramo console quando `RedirectTo` è l'indirizzo di questa console (vedi
 * `supabase/templates/README.md`). Toglierlo farebbe ricadere gli operatori
 * nell'email dell'app.
 *
 * L'ente si abilita a mano dopo la convenzione: `shouldCreateUser: false` lo
 * rende strutturale, non c'è nessuna registrazione da qui.
 *
 * L'esito del primo passo è **identico** per un indirizzo abilitato e per uno
 * sconosciuto — si passa al campo del codice in entrambi i casi. Distinguerli
 * regalerebbe a chiunque un modo per scoprire quali Comuni sono clienti, un
 * indirizzo alla volta; e chi un codice non l'ha ricevuto si ferma da sé al
 * passo dopo.
 */
export function Login() {
  const [passo, setPasso] = useState<'email' | 'codice'>('email');
  const [email, setEmail] = useState('');
  const [codice, setCodice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rimandato, setRimandato] = useState(false);

  async function chiediCodice(e?: FormEvent) {
    e?.preventDefault();
    if (!supabase || busy) return;
    setBusy(true);
    setError(null);

    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false, emailRedirectTo: siteUrl() },
    });

    setBusy(false);
    // L'unico errore che vale la pena distinguere è quello di rete, che
    // impedisce proprio di sapere se il codice è partito.
    if (err && /network|fetch|timeout/i.test(err.message)) {
      setError('Non riesco a contattare il servizio. Controlla la connessione.');
      return;
    }
    setPasso('codice');
  }

  async function entra(e: FormEvent) {
    e.preventDefault();
    if (!supabase || busy) return;
    setBusy(true);
    setError(null);

    /* `type: 'email'` copre sia il codice di chi rientra sia quello di chi
       conferma l'indirizzo la prima volta — è la stessa chiamata che l'app usa
       da mesi come ripiego al link (am-mobile/src/lib/auth.ts). */
    const { data, error: err } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: codice.trim().replace(/\s+/g, ''),
      type: 'email',
    });

    setBusy(false);
    if (err) {
      setError(
        /network|fetch|timeout/i.test(err.message)
          ? 'Non riesco a contattare il servizio. Controlla la connessione.'
          : 'Codice non valido o scaduto. Controlla di averlo copiato per intero, o chiedine un altro.',
      );
      return;
    }
    /* `verifyOtp` non lancia se la verifica risponde senza sessione: senza
       questo controllo la pagina si dichiarerebbe dentro e resterebbe fuori. */
    if (!data.session) {
      setError('Verifica riuscita ma senza sessione. Riprova.');
      return;
    }
    // Da qui in poi ci pensa `SessionProvider`: l'evento di Supabase monta la
    // console senza che questa schermata debba navigare da nessuna parte.
  }

  async function rimanda() {
    setCodice('');
    setRimandato(true);
    await chiediCodice();
  }

  return (
    <Shell
      sotto={
        <div className="mx-auto w-full max-w-3xl px-5">
          <AbilitaIlTuoComune />
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>Accedi per gestire il tuo Comune</CardTitle>
          <CardDescription>
            Inserisci la mail con cui è stata attivata l’utenza, riceverai un
            codice monouso per effettuare l’accesso.
          </CardDescription>
        </CardHeader>

        {passo === 'email' ? (
          <form onSubmit={chiediCodice}>
            <CardContent className="space-y-2">
              <Label htmlFor="email">Indirizzo email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                placeholder="nome.cognome@comune.esempio.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={Boolean(error) || undefined}
              />
              {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
            </CardContent>
            <CardFooter className="mt-6">
              <Button type="submit" size="lg" disabled={busy} className="w-full">
                {busy ? 'Invio…' : 'Ricevi codice'}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={entra}>
            <CardContent className="space-y-4">
              <Alert className="border-verde/40 bg-verde/10">
                <CheckCircle2 className="text-verde" />
                <AlertTitle>
                  {rimandato ? 'Ti abbiamo mandato un altro codice' : 'Controlla la posta'}
                </AlertTitle>
                <AlertDescription>
                  Se <span className="font-medium">{email.trim()}</span> è
                  abilitato, gli abbiamo mandato un codice monouso. Vale una
                  volta sola e scade dopo un’ora.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="codice">Codice ricevuto</Label>
                <Input
                  id="codice"
                  /* `text` e non `number`: un codice non è una quantità — con
                     `number` il browser offre le frecce, mangia gli zeri iniziali
                     e su desktop lo si può far scorrere con la rotella. */
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  placeholder="12345678"
                  className="tnum text-center text-[22px] tracking-[0.4em]"
                  value={codice}
                  onChange={(e) => setCodice(e.target.value)}
                  aria-invalid={Boolean(error) || undefined}
                />
                {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
              </div>
            </CardContent>
            <CardFooter className="mt-6 flex-col items-stretch gap-2">
              <Button type="submit" size="lg" disabled={busy} className="w-full">
                {busy ? 'Verifica…' : 'Entra'}
              </Button>
              <div className="flex justify-center gap-1">
                <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={rimanda}>
                  Rimanda il codice
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setPasso('email');
                    setCodice('');
                    setError(null);
                    setRimandato(false);
                  }}
                >
                  Usa un altro indirizzo
                </Button>
              </div>
            </CardFooter>
          </form>
        )}
      </Card>

      <p className="mt-5 text-[13px] leading-relaxed text-ink3">
        L’accesso è riservato agli operatori designati dal Comune. Per
        abilitarne uno nuovo, o revocarlo dopo un cambio di amministrazione,
        scrivi a chi ti ha dato l’accesso.
      </p>
    </Shell>
  );
}

/**
 * Chi arriva qui e un accesso non ce l'ha.
 *
 * Succede più di quanto sembri — un link inoltrato, un collega che cerca la
 * console del proprio Comune, un sindaco che ha visto la cosa altrove. Prima
 * per quella persona la pagina era un vicolo cieco: un campo che rifiuta il suo
 * indirizzo con un messaggio volutamente indistinguibile da quello di chi è
 * abilitato (vedi sopra: distinguerli direbbe a chiunque quali Comuni sono
 * clienti).
 *
 * Dal 02/09/2026 è una fascia chiusa, larga quanto la pagina, che si apre al
 * click. Chiusa perché chi entra tutti i giorni ha già il suo accesso e questa
 * roba non la deve rileggere ogni volta; larga perché è l'unica altra cosa che
 * si può fare qui dentro, e da riquadro sotto al modulo sembrava una nota a
 * piè di pagina.
 */
function AbilitaIlTuoComune() {
  const [aperto, setAperto] = useState(false);

  return (
    <Card className="mt-8 overflow-hidden p-0">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        aria-controls="abilita-comune"
        className="flex w-full items-center gap-4 px-6 py-5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[17px] font-semibold leading-snug text-ink">
            Abilita il tuo Comune
          </span>
          <span className="mt-0.5 block text-[14px] leading-relaxed text-ink3">
            Il canale della Protezione Civile comunale dentro AllertaMeteo,
            verso i cittadini che seguono il tuo Comune.
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={`size-5 shrink-0 text-ink4 transition-transform ${aperto ? 'rotate-180' : ''}`}
        />
      </button>

      {aperto ? (
        <div id="abilita-comune" className="space-y-6 border-t border-border px-6 pb-6 pt-6">
          <p className="text-[15px] leading-relaxed text-ink2">
            Scuole chiuse, strada interrotta, acqua non potabile, punto di
            raccolta, cessato allarme. Arrivano come notifica sul telefono e
            restano visibili in app finché sono validi.
          </p>
          <p className="text-[15px] font-medium leading-relaxed text-ink">
            Invia notifiche per avvisi importanti ai tuoi cittadini che usano
            l’app.
          </p>

          <div className="space-y-4">
            <Passo n="1" titolo="La richiesta">
              Attiviamo noi il Comune, previa richiesta, per gli operatori
              comunali che ci indicherete. Nessun account condiviso «Comune di
              X»: dopo un cambio di amministrazione si revoca la persona, non
              l’ente.
            </Passo>
            <Passo n="2" titolo="La convenzione">
              Riceverete via mail le istruzioni per proseguire nell’attivazione;
              firmerete una convenzione che riguarda l’erogazione gratuita del
              servizio e lo scarico di responsabilità.
            </Passo>
            <Passo n="3" titolo="L’abilitazione">
              Ogni operatore entra col proprio indirizzo istituzionale e firma
              quello che pubblica. Il contenuto resta del Comune.
            </Passo>
          </div>

          {/* Il tetto si dice PRIMA della firma, non dopo: è il vincolo che un
              ente scopre nel momento peggiore — quando ha appena scritto il
              terzo avviso della giornata. */}
          <p className="text-[13px] leading-relaxed text-ink3">
            Gli avvisi si pubblicano da qui, non dall’app, e le notifiche hanno
            un tetto giornaliero e settimanale, più alto quando è in corso
            un’allerta ufficiale sul territorio. Un avviso comunale non può
            dichiarare né implicare un colore di allerta: quello lo dice il
            bollettino della Protezione Civile regionale, e due voci sulla
            stessa scala non si leggono più.
          </p>

          <RichiestaAbilitazione />
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Il modulo della richiesta, e perché non ha un backend suo.
 *
 * Manda alla Edge Function `report-problem`, che è già in produzione, già
 * pubblica (`verify_jwt = false`, CORS aperto) e già inoltra via SES alla
 * casella che leggiamo — coi suoi tetti per dispositivo, per IP e globale. Una
 * function nuova avrebbe voluto secret SES suoi, un deploy suo e una migration
 * per il rate limit: per un modulo che riceve una richiesta a settimana sarebbe
 * stata infrastruttura da mantenere per sempre.
 *
 * Il prezzo, dichiarato: queste richieste arrivano nella stessa casella delle
 * segnalazioni dell'app. A distinguerle è il contesto, che porta `tipo:
 * Richiesta abilitazione Comune` come prima riga — la function lo rende riga
 * per riga senza conoscerne i campi, quindi aggiungerne uno qui non richiede di
 * toccare il backend.
 */
function RichiestaAbilitazione() {
  const [comune, setComune] = useState<ComuneScelto | null>(null);
  const [nome, setNome] = useState('');
  const [ruolo, setRuolo] = useState('');
  const [mail, setMail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [fatto, setFatto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function invia(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    // La selezione è forzata: senza un Comune dell'anagrafica non si manda
    // niente. Il bottone è già disabilitato, questo è il presidio che regge
    // anche a un invio da tastiera.
    if (!comune) {
      setError('Scegli il Comune dall’elenco.');
      return;
    }
    setBusy(true);
    setError(null);

    const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 20_000);
    try {
      const res = await fetch(`${base}/functions/v1/report-problem`, {
        method: 'POST',
        signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          message:
            `Richiesta di abilitazione per ${comune.nome_comune} (${comune.codice_istat}).` +
            (note.trim() ? `\n\n${note.trim()}` : ''),
          email: mail.trim(),
          deviceId: idRichiedente(),
          context: {
            tipo: 'Richiesta abilitazione Comune',
            comune: `${comune.nome_comune}${comune.provincia_sigla ? ` (${comune.provincia_sigla})` : ''}`,
            /* Il codice ISTAT è il motivo per cui il Comune si sceglie invece
               di scriverlo: chi riceve la richiesta lo incolla nel pannello e
               abilita l'ente giusto, senza dover indovinare quale «San
               Giovanni» sia. */
            codice_istat: comune.codice_istat,
            referente: nome.trim(),
            ruolo: ruolo.trim(),
            telefono: telefono.trim(),
            origine: 'console comunale — pagina di accesso',
          },
        }),
      });
      if (!res.ok) {
        // 429 è l'unico esito che ha una via d'uscita diversa da «riprova».
        setError(
          res.status === 429
            ? 'Troppe richieste da questa postazione. Riprova domani.'
            : 'Non è stato possibile mandare la richiesta. Riprova fra poco.',
        );
        return;
      }
      setFatto(true);
    } catch {
      setError('Nessuna connessione. Riprova fra poco.');
    } finally {
      clearTimeout(timer);
      setBusy(false);
    }
  }

  if (fatto) {
    return (
      <Alert className="border-verde/40 bg-verde/10">
        <CheckCircle2 className="text-verde" />
        <AlertTitle>Grazie della richiesta</AlertTitle>
        <AlertDescription>
          {/* L'indirizzo si ripete perché è l'unica cosa che chi ha appena
              scritto non può più rileggere: il modulo è sparito, e un refuso
              lì dentro è una risposta che non arriverà mai. */}
          Riceverai ulteriori indicazioni sulla mail{' '}
          <span className="font-medium text-ink">{mail.trim()}</span> quando
          processeremo la tua abilitazione.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={invia} className="space-y-4 rounded-lg border border-border bg-background p-5">
      <p className="text-[15px] font-semibold text-ink">Chiedi l’abilitazione</p>

      <SceltaComune scelto={comune} onScegli={setComune} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo id="ra-nome" label="Nome e cognome" value={nome} onChange={setNome} />
        <Campo id="ra-ruolo" label="Ruolo o ufficio" value={ruolo} onChange={setRuolo} placeholder="Protezione Civile" />
        <Campo
          id="ra-mail"
          label="Email istituzionale"
          value={mail}
          onChange={setMail}
          type="email"
          placeholder="nome.cognome@comune.esempio.it"
        />
        <Campo id="ra-tel" label="Telefono" value={telefono} onChange={setTelefono} type="tel" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ra-note">Note</Label>
        <Textarea
          id="ra-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1500}
        />
      </div>

      {error ? <p className="text-[13px] text-destructive">{error}</p> : null}

      <Button type="submit" disabled={busy || !comune}>
        {busy ? 'Invio…' : 'Invia richiesta'}
      </Button>
    </form>
  );
}

type ComuneScelto = {
  codice_istat: string;
  nome_comune: string;
  provincia_sigla: string | null;
};

/**
 * Il Comune si SCEGLIE, non si scrive.
 *
 * Un campo libero qui produceva richieste che poi bisognava interpretare —
 * «San Giovanni» sono undici Comuni, «Reggio» due — e chi le riceve deve
 * incollare un codice ISTAT nel pannello, non fare l'archivista. La lista
 * arriva da `public_comuni`, la stessa view che legge l'app: è pubblica in
 * lettura per `anon`, quindi la pagina di accesso può interrogarla senza aver
 * fatto l'accesso.
 *
 * Si cerca da due lettere in su, con un freno di 250 ms — non è un elenco da
 * scaricare tutto: sono 7.894 righe.
 */
function SceltaComune({
  scelto,
  onScegli,
}: {
  scelto: ComuneScelto | null;
  onScegli: (c: ComuneScelto | null) => void;
}) {
  const [testo, setTesto] = useState('');
  const [esiti, setEsiti] = useState<ComuneScelto[]>([]);
  const [cercando, setCercando] = useState(false);

  useEffect(() => {
    const q = testo.trim();
    if (scelto || q.length < 2 || !supabase) {
      setEsiti([]);
      return;
    }
    setCercando(true);
    const client = supabase;
    /* Il freno serve a due cose: non una richiesta per tasto, e non mostrare i
       risultati di «po» mentre si sta scrivendo «pontevico». Il cleanup di
       useEffect annulla il giro precedente a ogni lettera. */
    const t = setTimeout(() => {
      void client
        .from('public_comuni')
        .select('codice_istat,nome_comune,provincia_sigla')
        .ilike('nome_comune', `%${q}%`)
        .order('nome_comune')
        .limit(20)
        .then(({ data }) => {
          setEsiti((data as ComuneScelto[] | null) ?? []);
          setCercando(false);
        });
    }, 250);
    return () => {
      clearTimeout(t);
      setCercando(false);
    };
  }, [testo, scelto]);

  if (scelto) {
    return (
      <div className="space-y-2">
        <Label htmlFor="ra-comune">Comune</Label>
        <div className="flex items-center gap-3 rounded-md border border-input px-3 py-2">
          <span className="min-w-0 flex-1 text-[15px] text-ink">
            {scelto.nome_comune}
            {scelto.provincia_sigla ? (
              <span className="text-ink3"> ({scelto.provincia_sigla})</span>
            ) : null}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onScegli(null);
              setTesto('');
            }}
          >
            Cambia
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="ra-comune">Comune</Label>
      <Input
        id="ra-comune"
        autoComplete="off"
        placeholder="Cerca il tuo Comune — almeno due lettere…"
        value={testo}
        onChange={(e) => setTesto(e.target.value)}
      />
      {esiti.length ? (
        <ul className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border">
          {esiti.map((c) => (
            <li key={c.codice_istat}>
              <button
                type="button"
                onClick={() => onScegli(c)}
                className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-[15px] transition-colors hover:bg-muted"
              >
                <span className="min-w-0">{c.nome_comune}</span>
                <span className="shrink-0 text-[13px] text-ink4">
                  {c.provincia_sigla ?? ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {testo.trim().length >= 2 && !cercando && !esiti.length ? (
        <p className="text-[13px] text-ink3">Nessun Comune con questo nome.</p>
      ) : null}
    </div>
  );
}

/**
 * L'identificativo che regge il tetto per postazione della function.
 *
 * Non è un segreto e non prova niente — si azzera svuotando i dati del browser,
 * ed è esattamente come si comporta il gemello sull'app. La difesa vera resta
 * lato function: tetto per IP e interruttore globale.
 */
function idRichiedente(): string {
  const CHIAVE = 'am-console-richiesta';
  try {
    const salvato = localStorage.getItem(CHIAVE);
    if (salvato) return salvato;
    const nuovo = crypto.randomUUID();
    localStorage.setItem(CHIAVE, nuovo);
    return nuovo;
  } catch {
    // Storage negato (finestra privata, dati di sito bloccati): si manda
    // comunque, senza id. La function ha già la corsia per IP.
    return '';
  }
}

/** Tutti i campi della richiesta sono obbligatori: `required` non è un parametro. */
function Campo({
  id,
  label,
  value,
  onChange,
  type,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type ?? 'text'}
        required
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Passo({
  n,
  titolo,
  children,
}: {
  n: string;
  titolo: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <span
        aria-hidden
        className="tnum mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[13px] font-semibold text-primary-foreground"
      >
        {n}
      </span>
      <div className="min-w-0">
        <p className="text-[15px] font-semibold leading-snug text-ink">{titolo}</p>
        <p className="mt-0.5 text-[14px] leading-relaxed text-ink3">{children}</p>
      </div>
    </div>
  );
}

/**
 * La stessa cornice serve anche agli stati di errore fuori dal login.
 *
 * `sotto` esce dalla colonna stretta: la fascia «Abilita il tuo Comune» è larga
 * quanto la pagina, il modulo di accesso no.
 */
export function Shell({ children, sotto }: { children: ReactNode; sotto?: ReactNode }) {
  return (
    <div className="min-h-dvh">
      <SiteHeader />
      <div className="mx-auto w-full max-w-md px-5 pt-10">
        {/* Niente titolone sopra il box: il marchio in testa dice già dove si
            è, e ripeterlo in parole sarebbe la stessa cosa detta due volte.
            Cosa sia questa console lo spiega la riga dentro il box, dove serve
            a chi sta per digitare il proprio indirizzo. */}
        {children}
      </div>
      <div className="pb-16">{sotto}</div>
    </div>
  );
}
